/**
 * @typedef PrintOrderConfiguration Defines a PrintOrderConfigurationItem
 * @property {Object} print_partner_order_services the partner order services, including total & records
 */

/**
 * Given a printOrderConfiguration, retrieve the  print_partner_order_services available
 *
 * @param {PrintProductConfiguration} printOrderConfiguration the print order configuration
 * @returns {Array}
 */
export const getPrintPartnerOrderServices = printOrderConfiguration => {
  if (!printOrderConfiguration?.print_partner_order_services?.records) {
    return [];
  }

  return printOrderConfiguration.print_partner_order_services.records;
};

/**
 * Given a printOrderConfiguration, retrieve the related print order service labels
 *
 * @param {PrintProductConfiguration} printOrderConfiguration the print order configuration
 * @returns {Array}
 */
export const getPrintOrderServiceLabels = printOrderConfiguration => {
  if (!printOrderConfiguration?.print_partner_order_services?.records) {
    return [];
  }

  return printOrderConfiguration.print_partner_order_services.records.map(
    ({ print_partner_order_service }) =>
      print_partner_order_service.print_order_service.name,
  );
};

/**
 * Given a printOrderConfiguration, retrieve the related print order service ids
 *
 * @param {PrintProductConfiguration} printOrderConfiguration the print order configuration
 * @returns {Array}
 */
export const getPrintOrderSizeServiceIds = printOrderConfiguration => {
  if (!printOrderConfiguration?.print_partner_order_services?.records) {
    return [];
  }

  return printOrderConfiguration.print_partner_order_services.records.map(
    ({ print_partner_order_service }) => print_partner_order_service.id,
  );
};

/**
 * Given a printOrderConfiguration, determine if the config has the given printOrderService enabled
 *
 * @param {PrintProductConfiguration} printOrderConfiguration the print order configuration
 * @param {String} printOrderServiceId the print order service id
 * @returns {boolean}
 */
export const hasPrintOrderService = (
  printOrderConfiguration,
  printOrderServiceId,
) => {
  if (!printOrderConfiguration?.print_partner_order_services?.records) {
    return false;
  }

  return !!printOrderConfiguration.print_partner_order_services.records.find(
    ({ print_partner_order_service }) =>
      print_partner_order_service?.print_order_service?.id ===
      printOrderServiceId,
  );
};

/**
 * Given a printOrderConfiguration and orderServiceId, retrieve the price of the service.
 *
 * @param {PrintProductConfiguration} printOrderConfiguration the print order configuration
 * @param {String} printOrderServiceId the print order service id
 * @returns {number}
 */
export const getPrintOrderServicePrice = (
  printOrderConfiguration,
  printOrderServiceId,
) => {
  if (!printOrderConfiguration) {
    return 0;
  }

  const configPrintPartnerOrderService = (
    printOrderConfiguration.print_partner_order_services?.records ?? []
  ).find(
    ({ print_partner_order_service }) =>
      print_partner_order_service?.print_order_service?.id ===
      printOrderServiceId,
  );

  if (!configPrintPartnerOrderService) {
    return 0;
  }

  const {
    price,
    min_price: minPrice,
  } = configPrintPartnerOrderService.print_partner_order_service;

  if (minPrice?.value > 0) {
    return minPrice.value;
  }

  return price?.value ?? 0;
};

/**
 *
 * @param {PrintProductConfiguration} printOrderConfiguration the print order configuration
 * @param {Array} printOrderServiceIds the print order service ids
 * @returns {number}
 */
export const getPrintOrderServicesTotalPrice = (
  printOrderConfiguration,
  printOrderServiceIds,
) => {
  if (!printOrderConfiguration || (printOrderServiceIds?.length ?? 0) === 0) {
    return 0;
  }

  return printOrderServiceIds.reduce((sum, printOrderServiceId) => {
    return (
      sum +
      getPrintOrderServicePrice(printOrderConfiguration, printOrderServiceId)
    );
  }, 0);
};
